import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ROUTES } from "@/constants";
import { useAuth } from "@/hooks";
import {
  getManagerOriginForTenantSlug,
  resolveEffectiveTenantSlug,
} from "@/utils";
import { PageLoading } from "@/components/atoms";

interface RequireManagerTenantProps {
  children: React.ReactNode;
}

const MANAGER_SESSION_MESSAGE =
  "Tu sesión no tiene un espacio asignado.";

/**
 * Ensures MANAGER users operate within their assigned tenant context
 */
export const RequireManagerTenant: React.FC<RequireManagerTenantProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const location = useLocation();

  if (user?.role === "ADMIN") {
    return <>{children}</>;
  }

  if (user?.role !== "MANAGER") {
    return <Navigate to={ROUTES.public.home} replace />;
  }

  if (!user.tenantId || !user.tenantSlug) {
    return (
      <Navigate
        to={ROUTES.backoffice.login}
        replace
        state={{
          message: MANAGER_SESSION_MESSAGE,
          from: location,
        }}
      />
    );
  }

  const effectiveSlug = resolveEffectiveTenantSlug(window.location.hostname);

  if (user.tenantSlug !== effectiveSlug) {
    return <ManagerTenantRedirect tenantSlug={user.tenantSlug} />;
  }

  return <>{children}</>;
};

interface ManagerTenantRedirectProps {
  tenantSlug: string;
}

const ManagerTenantRedirect: React.FC<ManagerTenantRedirectProps> = ({
  tenantSlug,
}) => {
  useEffect(() => {
    window.location.replace(
      `${getManagerOriginForTenantSlug(tenantSlug)}${ROUTES.backoffice.manager}`,
    );
  }, [tenantSlug]);

  return <PageLoading />;
};
